import React, { useEffect } from 'react';
import {
    View,
    Image
} from 'react-native';

import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLocationDot, IconDefinition } from '@fortawesome/free-solid-svg-icons';
import { translate } from '../../services/translate';
import MyAppText from '../../components/MyAppText';
import personDefaultPicture from '../../../assets/person-default-picture.png';
import { faCalendarCheck } from '@fortawesome/free-regular-svg-icons';
import getBestContrastColor from '../../services/best-contrast-color';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { faCameraCctv } from '@fortawesome/pro-solid-svg-icons';
import MyInfinityDataTable from '../MyInfinityDataTable';
import { RootNavigation } from '../../Routes';


interface EventListProps {
    events: FacialDetection[];
    navigation: RootNavigation;
    isLoading: boolean;
    loadMore?: () => void;
    module: 'facial' | 'smart-search-facial';
    page: 'live-events' | 'live-alerts' | 'search';
}


export default function EventList({ events, navigation, isLoading, loadMore, module, page }: EventListProps) {
    const { styles, theme } = useStyles(stylesheet);

    useEffect(() => {
        for (const event of events) {
            if (event.person) {
                event.person.facePictures = event.person.facePictures.filter(fp => {
                    const eventCreatedAt = moment(event.createdAt);
                    return moment(fp.createdAt).isBefore(eventCreatedAt) &&
                        (fp.deletedAt === null || moment(fp.deletedAt).isAfter(eventCreatedAt));
                });
            }
        }
    }, [events]);

    return <MyInfinityDataTable
        onRowPress={row => {
            if (module == 'facial' && page == 'live-alerts') {
                return navigation.navigate('Facial', {
                    screen: 'LiveAlertEventDetail', params: {
                        eventId: row.id,
                    }
                });
            }
            if (module == 'facial' && page == 'live-events') {
                return navigation.navigate('Facial', {
                    screen: 'LiveEventDetail', params: {
                        eventId: row.id,
                    }
                });
            }
            if (module == 'facial' && page == 'search') {
                return navigation.navigate('Facial', {
                    screen: 'SearchEventDetail', params: {
                        eventId: row.id,
                    }
                });
            }
        }}
        progressPending={isLoading}
        loadMore={loadMore || (() => { })}
        data={events}
        columns={
            [{
                name: translate('detection'),
                cell: row => <View style={{ paddingVertical: 8 }}>
                    <img
                        style={{
                            height: 100,
                            width: 90,
                            borderRadius: 5,
                            overflow: 'hidden',
                            filter: page === 'live-events' || page === 'live-alerts' ? 'blur(5px)' : '',
                        }}
                        src={row.imageUrl}
                    />
                </View>,
                width: 120,
            }, {
                name: translate('reference'),
                cell: row => <View style={{ paddingVertical: 8 }}>
                    <Image
                        style={{
                            height: 100,
                            width: 90,
                            borderRadius: 5,
                            overflow: 'hidden',
                        }}
                        source={row.person?.facePictures.length ? { uri: row.person.facePictures[0].imageUrl } : personDefaultPicture}
                    />
                </View>,
                width: 120,
            }, {
                name: translate('information'),
                cell: row => <View style={{ paddingVertical: 8, rowGap: 10 }}>
                    {row.person
                        ? <>
                            <MyAppText style={{ color: theme.colors.field.text, fontWeight: '500', fontSize: 20 }}>
                                {row.person.name}
                            </MyAppText>
                            <View style={{ flexDirection: 'row' }}>
                                {row.person.markers.map((marker) =>
                                    <View key={JSON.stringify(marker)} style={[styles.marker, { backgroundColor: marker.color }]}>
                                        <MyAppText style={styles.markerText(marker.color)}>
                                            {marker.name}
                                        </MyAppText>
                                    </View>
                                )}
                            </View>
                        </>
                        : null
                    }
                    {row.confidence &&
                        <View style={styles.locationView} >
                            <MyAppText style={styles.locationText}>
                                {translate('confidence')}: {Math.floor((Number(row.confidence) * 100)).toFixed(0)}%
                            </MyAppText>
                        </View>
                    }
                </View>,
                flex: 3,
            }, {
                name: translate('location'),
                cell: row => <View style={{ paddingVertical: 8, rowGap: 10 }}>
                    <View style={styles.locationView} >
                        <FontAwesomeIcon icon={faCalendarCheck} fontSize={13} style={styles.locationIcon} />
                        <MyAppText style={styles.locationText}>
                            {moment(new Date(row.time)).format('DD/MM/YYYY HH:mm:ss')}
                        </MyAppText>
                    </View>

                    <View style={styles.locationView} >
                        <FontAwesomeIcon icon={faLocationDot} fontSize={13} style={styles.locationIcon} />
                        <MyAppText style={styles.locationText}>
                            {row.camera.address}
                        </MyAppText>
                    </View>

                    <View style={styles.locationView} >
                        <FontAwesomeIcon icon={faCameraCctv as IconDefinition} fontSize={13} style={styles.locationIcon} />
                        <MyAppText style={styles.locationText}>
                            {row.camera.title}
                        </MyAppText>
                    </View>
                </View>,
                flex: 1,
            },]}
    />;

}

const stylesheet = createStyleSheet(theme => ({
    container: {
        flex: 1,
        rowGap: 15,
        zIndex: -9999
    },
    header: {
        flexDirection: 'row',
    },
    headerCell: {
        fontSize: 13,
        color: theme.colors.labelColor
    },
    locationView: {
        flexDirection: 'row',
        alignItems: 'center',
        columnGap: 5
    },
    locationIcon: {
        color: theme.colors.textColor,
    },
    locationText: {
        color: theme.colors.card.detailText,
        fontWeight: '500',
    },
    row: {
        flexDirection: 'row',
        rowGap: 15
    },
    loadMoreText: {
        color: theme.colors.primaryButton.background,
        fontSize: 16,
    },
    loadMore: {
        borderColor: theme.colors.primaryButton.background,
        alignItems: 'center',
        justifyContent: 'center'
    },
    marker: {
        borderRadius: 8,
        height: 16,
        paddingLeft: 10,
        paddingRight: 10,
        marginRight: 8,
    },
    markerText: (tagColor: string) => ({
        fontWeight: '500',
        color: getBestContrastColor(tagColor),
        fontSize: 11
    }),
    cardList: {
        borderWidth: 1,
        borderRadius: 8,
        borderColor: theme.colors.container.border,
        padding: 24,
        backgroundColor: theme.colors.container.background,
        gap: 20,
        flex: 1
    },
    tableLine: {
        borderBottomColor: theme.colors.container.border,
        borderBottomWidth: 1,
    },
}));
